<template>
  <nav
    class="navbar relative grid grid-cols-[auto,1fr,auto] items-center justify-center border-y-[0.5px] border-solid border-neutral-400 bg-secondary-50 px-[186px] py-0.5"
  >
    <IconButton
      name="ic-arrow-left"
      :aria-label="$t('common.actions.previous')"
      :c_style="{ type: 'ghost' }"
      :class="{
        invisible: !swiperBtnsVisibility.prev,
      }"
      @click="
        mySwiper?.slideTo(
          Math.max(
            mySwiper.activeIndex - mySwiper?.slidesPerViewDynamic() - 1,
            0,
          ),
        )
      "
    />

    <div id="navbar-swiper" class="swiper navbar-swiper !m-0">
      <div v-show="!swiperLoading" class="swiper-wrapper">
        <div
          v-if="$features?.categories?.is_enabled"
          v-for="category in categories"
          class="swiper-slide !relative !w-fit select-none !overflow-visible"
        >
          <Button
            :test-id="TEST_IDS.CATEGORY_LINK"
            :label="category?.title"
            :c_style="{
              type: 'text',
              style: 'B',
            }"
            :link="{
              path: {
                path: paths.products,
                query: {
                  filter_bar: $route.query.filter_bar,
                  categories: category?.id ?? '',
                },
              },
            }"
            @click="
              fetchFilters(category.id);
              fetchBrands(category.id);
            "
            @mouseleave="startCloseTimer"
            @mouseenter="handleShowNavMenu($event, 'categories', category)"
          />
        </div>

        <div
          v-if="$features?.brands?.is_enabled"
          class="swiper-slide !relative !w-fit select-none !overflow-visible"
        >
          <Button
            label="Brands"
            :test-id="TEST_IDS.BRANDS_LINK"
            :c_style="{
              type: 'text',
              style: 'B',
            }"
            :link="{
              path: {
                path: paths.products,
                query: {
                  filter_bar: $route.query.filter_bar,
                },
              },
            }"
            @click="
              fetchFilters();
              fetchBrands();
            "
            @mouseleave="startCloseTimer"
            @mouseenter="handleShowNavMenu($event, 'brands')"
          />
        </div>

        <div
          v-if="$features?.bundles?.is_enabled"
          class="swiper-slide !relative !w-fit select-none !overflow-visible"
        >
          <Button
            label="Bundles"
            :c_style="{
              type: 'text',
              style: 'B',
            }"
            :link="{ path: paths.bundles }"
          />
        </div>
      </div>

      <div v-show="swiperLoading" class="swiper-wrapper gap-5">
        <div
          v-for="_ in 15"
          class="h-8 min-w-24 animate-pulse rounded bg-neutral-400"
        />
      </div>
    </div>

    <IconButton
      name="ic-arrow-right"
      :aria-label="$t('common.actions.next')"
      :c_style="{ type: 'ghost' }"
      :class="{
        invisible: !swiperBtnsVisibility.next,
      }"
      @click="
        mySwiper?.slideTo(
          Math.min(
            mySwiper.activeIndex + mySwiper?.slidesPerViewDynamic() - 1,
            mySwiper?.slides.length - 1,
          ),
        )
      "
    />

    <CategoryMenu
      v-if="selectedCategory?.children?.length"
      class="absolute top-full z-20 max-h-[450px] max-w-[350px]"
      :test-id="TEST_IDS.CATEGORIES_MENU"
      :categories="navMenuItems"
      :style="{
        left: `${position}px`,
      }"
      @select="handleFilter"
      @mouseenter="handelClearTimer"
      @mouseleave="closeMenu"
    />

    <BrandsMenu
      v-else-if="selectedBrands"
      id="brands-menu"
      class="absolute top-full z-20 max-h-[450px]"
      :class="(navMenuItems?.length ?? 0) > 9 ? 'w-[310px]' : 'w-[155px]'"
      :brands="navMenuItems"
      :style="{
        left: `${position}px`,
      }"
      @select="handleFilter"
      @mouseenter="handelClearTimer"
      @mouseleave="closeMenu"
    />
  </nav>
</template>

<script setup lang="ts">
import type { NavItemProps } from "~/types/schemas/Informative/Molecule/NavItem";
import type { Category } from "~/types/Data/ProductManagement/Categories";
import { Swiper } from "swiper";
import useCategories from "~/composables/ProductManagement/useCategories";
import paths from "~/constants/paths";
import Button from "~/components/common/Atom/Button.vue";
import IconButton from "~/components/common/Atom/IconButton.vue";
import CategoryMenu from "~/components/features/InformativePages/Molecule/CategoryMenu/Desktop.vue";
import BrandsMenu from "~/components/features/InformativePages/Molecule/BrandsMenu.vue";
import useBrands from "~/composables/ProductManagement/useBrands";
import "swiper/css";
import useFilters from "~/composables/ProductManagement/useFilters";
import TEST_IDS from "~~/tests/constants/ids";

let closeTimeoutId: NodeJS.Timeout;

const { categories } = useCategories();
const { brands, fetchBrands } = useBrands();
const { fetchFilters } = useFilters();
const nuxtApp = useNuxtApp();
const router = useRouter();
const route = useRoute();

const mySwiper = shallowRef<Swiper>();
const selectedCategory = ref<Category>();
const selectedBrands = ref(false);
const position = ref<number>();

const swiperLoading = computed(
  () => !!nuxtApp.$features?.categories?.is_enabled && !categories.value,
);

const swiperBtnsVisibility = ref({
  prev: false,
  next: false,
});

const buildLinkPath = (
  qKey: string,
  value: string,
  mainCategoryId?: string,
  parentId?: string,
) => {
  const query: Record<string, any> = {
    filter_bar: route.query.filter_bar,
    categories: mainCategoryId,
    [qKey]: value,
  };

  if (qKey === "collections" && parentId) {
    query.sub_categories = parentId;
  }

  return {
    path: paths.products,
    query,
  };
};

//TODO: refactor nav items when manipulations confirming with design team weather the brands will be placed in the same
const navMenuItems = computed<NavItemProps[] | undefined>(() => {
  if (selectedCategory.value) {
    return selectedCategory.value?.children?.map((sub) => ({
      q_key: "sub_categories",
      value: sub.id,
      label: sub?.title,
      img: sub?.category_image,
      parent_id: sub.parent_category?.id,
      link: buildLinkPath("sub_categories", sub.id, selectedCategory.value?.id),
      children: sub?.children?.map((child) => ({
        q_key: "collections",
        value: child.id,
        label: child?.title ?? "",
        img: child.category_image,
        parent_id: sub.id,
        link: buildLinkPath(
          "collections",
          child.id,
          selectedCategory.value?.id,
          sub.id,
        ),
      })),
    }));
  } else if (selectedBrands.value) {
    return brands.value?.map((brand) => ({
      q_key: "brands",
      value: brand.id,
      label: brand.title,
      img: brand.logo,
      link: buildLinkPath("brands", brand.id),
    }));
  }
});

const handleShowNavMenu = (
  event: MouseEvent,
  key: "categories" | "brands",
  category?: Category,
) => {
  handelClearTimer();
  const rect = (event.target as HTMLDivElement).getBoundingClientRect();

  if (key === "categories") {
    selectedCategory.value = category;
    position.value = rect.left - 25; //subtract 25px from the position to center the menu
  } else if (key === "brands") {
    selectedCategory.value = undefined;
    selectedBrands.value = true;
    if ((brands.value?.length ?? 0) <= 9) {
      position.value = rect.left - 25; //subtract 25px from the position to center the menu
    } else {
      position.value = rect.left - (310 - rect.width) / 2;
    }
  }
};

const startCloseTimer = () => {
  closeTimeoutId = setTimeout(() => {
    closeMenu();
  }, 220);
};

const handelClearTimer = () => clearTimeout(closeTimeoutId);

const closeMenu = () => {
  position.value = undefined;
  selectedCategory.value = undefined;
  selectedBrands.value = false;
};

const handleFilter = (
  key: string,
  value: string,
  associatedItem?: NavItemProps,
  parent_id?: string,
) => {
  const query: Record<string, any> = {
    filter_bar: router.currentRoute.value.query.filter_bar,
    categories: parent_id,
    [key]: value,
  };

  if (associatedItem) query[associatedItem.q_key] = associatedItem.value;

  fetchFilters(Object.values(query).join(","));
  fetchBrands(parent_id);

  closeMenu();
};

const setSwiperBtnVisibility = (viewPrev: boolean, viewNext: boolean) => {
  swiperBtnsVisibility.value.prev = viewPrev;
  swiperBtnsVisibility.value.next = viewNext;
};

onMounted(() => {
  mySwiper.value = new Swiper(".navbar-swiper", {
    slidesPerView: "auto",
    spaceBetween: "48px",
    grabCursor: true,
    centerInsufficientSlides: true,
  });

  mySwiper.value.on("slidesUpdated", (swiper) => {
    const needsNavigation =
      swiper.slides.length > swiper.slidesPerViewDynamic();
    setSwiperBtnVisibility(false, needsNavigation);
  });

  mySwiper.value.on("slideChange", () => {
    setSwiperBtnVisibility(
      !mySwiper.value?.isBeginning,
      !mySwiper.value?.isEnd,
    );
  });

  nextTick(() => {
    if (!mySwiper.value) return;

    mySwiper.value.update();

    const needsNavigation =
      mySwiper.value.slides.length > mySwiper.value.slidesPerViewDynamic();

    setSwiperBtnVisibility(
      needsNavigation && !mySwiper.value.isBeginning,
      needsNavigation && !mySwiper.value.isEnd,
    );
  });
});
</script>

<style>
.navbar {
  .btn-label {
    @apply text-ellipsis text-nowrap text-start;
  }
}
</style>
